<template>
    <div class="refund-cancel-auto-renew">
        <div class="block-header">
            <h3 class="block-title">Auto-Renewal Paused</h3>
            <p class="block-description">
                Your auto-renewal has been successfully paused. You'll keep access until your current billing period ends.
            </p>
        </div>

        <div class="pause-details">
            <div class="info-card">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">Auto-Renewal Status</h4>
                </div>
                <div class="card-content">
                    <div class="status-info">
                        <div class="status-row">
                            <span class="status-label">Current Status:</span>
                            <span class="status-value text-orange-500">Paused</span>
                        </div>
                        <div class="status-row">
                            <span class="status-label">Access Until:</span>
                            <span class="status-value" :class="{ 'text-red-500': !dialogRef.data?.renew }">
                                {{ getEndDate() }}
                                <svg v-if="!dialogRef.data?.renew" 
                                     class="w-4 h-4 ml-1 inline cursor-help" 
                                     fill="currentColor" 
                                     viewBox="0 0 20 20"
                                     title="This license is set to not renew">
                                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                </svg>
                            </span>
                        </div>
                        <div class="status-row">
                            <span class="status-label">Next Billing:</span>
                            <span class="status-value">Cancelled</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="info-card">
                <div class="card-header">
                    <h4 class="card-title">What This Means</h4>
                </div>
                <div class="card-content">
                    <ul class="implications">
                        <li>You'll continue to have full access to all services until {{ getEndDate() }}</li>
                        <li>No further charges will be made to your payment method</li>
                        <li>You can reactivate auto-renewal at any time</li>
                        <li>Your account will remain active but may have limited features after the end date</li>
                    </ul>
                </div>
            </div>

            <div class="info-card">
                <div class="card-header">
                    <h4 class="card-title">Need to Reactivate?</h4>
                </div>
                <div class="card-content">
                    <p class="mb-3">If you change your mind, you can easily reactivate your subscription:</p>
                    <ul class="reactivation-steps">
                        <li>Go to your account settings</li>
                        <li>Click on "Billing"</li>
                        <li>Select "Reactivate Auto-Renewal" on your license under "Manage Licenses"</li>
                        <li>Your subscription will continue as normal</li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="action-buttons">
            
            <button 
                class="btn btn-primary"
                @click=" emit('close')"
            >
            Close
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import moment from 'moment';
import { useLicenseStore } from '~/stores/person/licenses';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.REFUND_LICENSE);

const emit = defineEmits<{
    close: [];
    goToPage: [page: number];
}>();

const getEndDate = () => {
    if (!dialogRef.value?.data?.next_bill_date) {
        return 'Unknown';
    }
    
    return moment(dialogRef.value?.data?.next_bill_date).format('MMMM D, YYYY');
};



const updateLicenseAutoRenewal = async () => {
    const licenseStore = useLicenseStore();
    const authStore = useAuthStore();

    await licenseStore.updateLicenseRenewal(
        authStore.user?.identity?.id!,
        dialogRef.value?.data?.id!,
        false
    );
};

onMounted(() =>{
    updateLicenseAutoRenewal();
});

</script>

<style lang="scss" scoped>
.refund-cancel-auto-renew {
    @apply p-6;
}

.block-header {
    @apply mb-6 text-center;
    
    .block-title {
        @apply text-2xl font-semibold text-gray-900 mb-2;
    }
    
    .block-description {
        @apply text-gray-600 text-base;
    }
}

.pause-details {
    @apply mb-6 space-y-4;
}

.info-card {
    @apply bg-white rounded-lg border border-gray-200 p-6;
    
    .card-header {
        @apply flex items-center mb-4;
        
        .card-title {
            @apply text-lg font-semibold text-gray-900;
        }
    }
    
    .card-content {
        .status-info {
            .status-row {
                @apply flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0;
                
                .status-label {
                    @apply font-medium text-gray-700;
                }
                
                .status-value {
                    @apply text-gray-900;
                }
            }
        }
        
        .implications,
        .reactivation-steps {
            @apply list-disc list-inside space-y-2 text-gray-700;
        }
    }
}

.action-buttons {
    @apply flex justify-center gap-3;
    
    .btn {
        @apply px-6 py-2 rounded-lg font-medium transition-all duration-200;
        
        &.btn-outline {
            @apply border border-gray-300 text-gray-700 bg-white;
            
            &:hover:not(:disabled) {
                @apply border-gray-400 bg-gray-50;
            }
            
            &:disabled {
                @apply opacity-50 cursor-not-allowed;
            }
        }
        
        &.btn-primary {
            @apply bg-blue-500 text-white border border-blue-500;
            
            &:hover:not(:disabled) {
                @apply bg-blue-600 border-blue-600;
            }
            
            &:disabled {
                @apply opacity-50 cursor-not-allowed;
            }
        }
    }
}
</style> 